import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENQUIRING_AS = ["School", "Parent", "Family", "Individual", "Corporate / Group", "Other"];

const JOURNEY_OPTIONS = [
  { value: "tide-trails-goa", label: "Tide Trails, Goa" },
  { value: "lessons-from-the-wild-pench", label: "Lessons from the Wild, Pench" },
  { value: "bespoke-experiences", label: "Bespoke Experiences" },
  { value: "coming-soon", label: "A Coming Soon destination" },
  { value: "not-sure", label: "Not sure yet" },
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  organisation: "",
  enquiring_as: "",
  journey: "",
  grade: "",
  group_size: "",
  preferred_dates: "",
  message: "",
  website: "",
};

const inputClass =
  "w-full border border-line bg-white px-4 py-3 text-base text-ink placeholder:text-ink/40 transition-colors focus:border-pine focus:outline-none";
const labelClass = "mb-2 block text-sm font-semibold text-ink";

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-2 text-sm font-medium text-red-700">
      {message}
    </p>
  );
}

export default function EnquiryForm() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState("");
  const [available, setAvailable] = useState(null);

  useEffect(() => {
    const preselect = searchParams.get("journey");
    if (preselect && JOURNEY_OPTIONS.some((o) => o.value === preselect)) {
      setForm((f) => ({ ...f, journey: preselect }));
    }
  }, [searchParams]);

  useEffect(() => {
    axios
      .get(`${API}/enquiry/status`)
      .then((res) => setAvailable(Boolean(res.data?.available)))
      .catch(() => setAvailable(false));
  }, []);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please tell us your name.";
    if (!form.email.trim()) next.email = "Please share your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "That email address does not look quite right.";
    if (!form.phone.trim()) next.phone = "Please share a phone number we can reach you on.";
    else if (!/^[+0-9][0-9\s-]{6,19}$/.test(form.phone.trim()))
      next.phone = "That phone number does not look quite right.";
    if (!form.message.trim()) next.message = "Please write a few words about what you have in mind.";
    setErrors(next);
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      document.getElementById(`field-${Object.keys(validationErrors)[0]}`)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      setSuccess(true);
    } catch (err) {
      setServerError(
        err.response?.data?.detail ||
          "Your enquiry could not be sent just now. Please try again in a little while. Your details below have been kept.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (available === false) {
    return (
      <div className="border border-line bg-white p-8" data-testid="enquiry-unavailable">
        <h2 className="text-xl font-medium">Enquiries are temporarily unavailable</h2>
        <p className="mt-3 text-base leading-relaxed text-ink/75">
          Our enquiry service is not reachable at the moment. Nothing has been sent. Please try
          again later, or call us on the number shown beside this form.
        </p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="border border-pine/30 bg-white p-8 sm:p-10" role="status" data-testid="enquiry-success">
        <CheckCircle2 className="h-8 w-8 text-pine" aria-hidden="true" />
        <h2 className="mt-5 text-2xl font-medium tracking-tight">Thank you. Your enquiry is on its way.</h2>
        <p className="mt-3 max-w-lg text-base leading-relaxed text-ink/75">
          We have received your message and will be in touch soon. If anything changes in the
          meantime, you are welcome to send another note.
        </p>
        <button
          type="button"
          onClick={() => {
            setForm(EMPTY);
            setSuccess(false);
          }}
          data-testid="enquiry-send-another-button"
          className="mt-8 border border-pine px-6 py-3 text-base font-medium text-pine transition-colors hover:bg-pine hover:text-canvas"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="enquiry-form" className="space-y-7">
      <div className="honeypot" aria-hidden="true">
        <label htmlFor="field-website">Website</label>
        <input
          id="field-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set("website")}
        />
      </div>

      <div className="grid gap-7 sm:grid-cols-2">
        <div>
          <label htmlFor="field-name" className={labelClass}>
            Name <span aria-hidden="true" className="text-pine">*</span>
          </label>
          <input
            id="field-name"
            data-testid="enquiry-name-input"
            type="text"
            required
            aria-required="true"
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? "error-name" : undefined}
            maxLength={120}
            autoComplete="name"
            className={inputClass}
            value={form.name}
            onChange={set("name")}
          />
          <FieldError id="error-name" message={errors.name} />
        </div>
        <div>
          <label htmlFor="field-email" className={labelClass}>
            Email <span aria-hidden="true" className="text-pine">*</span>
          </label>
          <input
            id="field-email"
            data-testid="enquiry-email-input"
            type="email"
            required
            aria-required="true"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "error-email" : undefined}
            maxLength={200}
            autoComplete="email"
            className={inputClass}
            value={form.email}
            onChange={set("email")}
          />
          <FieldError id="error-email" message={errors.email} />
        </div>
        <div>
          <label htmlFor="field-phone" className={labelClass}>
            Phone <span aria-hidden="true" className="text-pine">*</span>
          </label>
          <input
            id="field-phone"
            data-testid="enquiry-phone-input"
            type="tel"
            required
            aria-required="true"
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={errors.phone ? "error-phone" : undefined}
            maxLength={40}
            autoComplete="tel"
            className={inputClass}
            value={form.phone}
            onChange={set("phone")}
          />
          <FieldError id="error-phone" message={errors.phone} />
        </div>
        <div>
          <label htmlFor="field-organisation" className={labelClass}>
            School / Organisation <span className="font-normal text-ink/50">(optional)</span>
          </label>
          <input
            id="field-organisation"
            data-testid="enquiry-organisation-input"
            type="text"
            maxLength={160}
            autoComplete="organization"
            className={inputClass}
            value={form.organisation}
            onChange={set("organisation")}
          />
        </div>
        <div>
          <label htmlFor="field-enquiring-as" className={labelClass}>
            I am enquiring as <span className="font-normal text-ink/50">(optional)</span>
          </label>
          <select
            id="field-enquiring-as"
            data-testid="enquiry-enquiring-as-select"
            className={inputClass}
            value={form.enquiring_as}
            onChange={set("enquiring_as")}
          >
            <option value="">Select one</option>
            {ENQUIRING_AS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="field-journey" className={labelClass}>
            Journey / Destination <span className="font-normal text-ink/50">(optional)</span>
          </label>
          <select
            id="field-journey"
            data-testid="enquiry-journey-select"
            className={inputClass}
            value={form.journey}
            onChange={set("journey")}
          >
            <option value="">Select one</option>
            {JOURNEY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="field-grade" className={labelClass}>
            Grade / Age Group <span className="font-normal text-ink/50">(optional)</span>
          </label>
          <input
            id="field-grade"
            data-testid="enquiry-grade-input"
            type="text"
            maxLength={80}
            className={inputClass}
            value={form.grade}
            onChange={set("grade")}
          />
        </div>
        <div>
          <label htmlFor="field-group-size" className={labelClass}>
            Approximate Group Size <span className="font-normal text-ink/50">(optional)</span>
          </label>
          <input
            id="field-group-size"
            data-testid="enquiry-group-size-input"
            type="text"
            maxLength={80}
            className={inputClass}
            value={form.group_size}
            onChange={set("group_size")}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="field-preferred-dates" className={labelClass}>
            Preferred Dates <span className="font-normal text-ink/50">(optional)</span>
          </label>
          <input
            id="field-preferred-dates"
            data-testid="enquiry-preferred-dates-input"
            type="text"
            maxLength={120}
            placeholder="For example, early December 2026"
            className={inputClass}
            value={form.preferred_dates}
            onChange={set("preferred_dates")}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="field-message" className={labelClass}>
            Message <span aria-hidden="true" className="text-pine">*</span>
          </label>
          <textarea
            id="field-message"
            data-testid="enquiry-message-input"
            required
            aria-required="true"
            aria-invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? "error-message" : undefined}
            maxLength={4000}
            rows={6}
            className={`${inputClass} resize-y`}
            value={form.message}
            onChange={set("message")}
          />
          <FieldError id="error-message" message={errors.message} />
        </div>
      </div>

      {serverError && (
        <div
          role="alert"
          data-testid="enquiry-error"
          className="border border-red-300 bg-red-50 px-5 py-4 text-base text-red-800"
        >
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || available !== true}
        data-testid="enquiry-submit-button"
        className="inline-flex items-center justify-center bg-pine px-8 py-4 text-base font-medium text-canvas transition-colors duration-200 hover:bg-pinedeep disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Sending..." : "Send Enquiry"}
      </button>
    </form>
  );
}
